import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';

// Fix R-A2041: replace only the leading '#' so token values are never truncated
const parseHashParams = (hash) => new URLSearchParams(hash.replace(/^#/, '?'));

export default function Login({ isSignUpRoute = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(isSignUpRoute);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "StudioSign | Login";
  }, []);

  useEffect(() => {
    setIsSignUp(isSignUpRoute);
    setError(null);
    setAgreedToTerms(false);
  }, [isSignUpRoute]);

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        navigate('/update-password', { replace: true });
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        const hash = window.location.hash;
        // Fix R-A2041 (Login.jsx:52): use parseHashParams to replace only the leading '#'
        const hashParams = parseHashParams(hash);
        const type = hashParams.get('type');

        if (type === 'recovery') return;

        if (type === 'signup' || type === 'email') {
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/welcome', { replace: true });
          return;
        }
      }
    });

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        // Fix R-A2041 (Login.jsx:73): use parseHashParams to replace only the leading '#'
        const hashParams = parseHashParams(window.location.hash);
        if (hashParams.get('type') === 'recovery') return;

        if (session) {
          navigate('/admin', { replace: true });
        } else {
          setInitializing(false);
        }
      }
    };
    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    if (isSignUp && !agreedToTerms) return;

    setLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });
        if (signUpError) throw signUpError;
        setSignUpSuccess(true);
        setLoading(false);
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) { setResetError("Please enter your email address."); return; }
    setResetLoading(true);
    setResetError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  if (initializing) {
    return <div className="min-h-screen bg-zinc-950" />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center relative overflow-hidden">
      <div className="absolute w-[600px] h-[600px] bg-white/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-10 flex flex-col relative z-10 mx-4">
        {signUpSuccess ? (
          <div className="flex flex-col items-center text-center py-6">
            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Check your inbox.</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-8">
              A verification link has been sent to your email. Please confirm your account to continue.
            </p>
            <button
              type="button"
              onClick={() => { setSignUpSuccess(false); setEmail(''); setPassword(''); navigate('/login'); }}
              className="text-sm font-medium text-zinc-500 hover:text-white transition-colors duration-300 ease-out"
            >
              Return to login
            </button>
          </div>
        ) : isResetting && resetSent ? (
          <div className="flex flex-col items-center text-center py-6">
            <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Reset link sent.</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-8">
              If an account exists for that email, we've sent instructions to reset your password.
            </p>
            <button
              type="button"
              onClick={() => { setIsResetting(false); setResetSent(false); setResetError(null); setEmail(''); setPassword(''); navigate('/login'); }}
              className="text-sm font-medium text-zinc-500 hover:text-white transition-colors duration-300 ease-out"
            >
              Return to login
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-3xl tracking-tight text-center flex items-center justify-center">
              {isSignUp && !isResetting && <span className="font-medium text-zinc-400 mr-2">Join</span>}
              {isResetting && <span className="font-medium text-zinc-400 mr-2">Recover</span>}
              <span className="font-medium text-zinc-400">Studio</span>
              <span className="font-extrabold text-white">Sign</span>
            </h1>
            <p className="text-sm font-medium text-zinc-400 text-center mt-2 mb-8">
              {isResetting
                ? 'Enter your email to receive a reset link.'
                : isSignUp ? 'Create an account to manage your live displays.' : 'Sign in to manage your live displays.'}
            </p>

            {isResetting ? (
              <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
                <input
                  type="email"
                  placeholder="operator@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={resetLoading}
                  className="w-full bg-black/50 border border-white/5 rounded-xl px-4 py-3 text-white focus:border-white/20 outline-none transition-colors duration-300 ease-out placeholder:text-zinc-600 disabled:opacity-50"
                />
                {resetError && <div className="text-red-400 text-sm font-medium text-center">{resetError}</div>}
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-white text-black font-semibold tracking-wide py-3.5 rounded-xl mt-4 hover:bg-zinc-200 transition-colors duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button
                  type="button"
                  onClick={() => { setIsResetting(false); setResetError(null); }}
                  disabled={resetLoading}
                  className="text-xs text-zinc-500 hover:text-white transition-colors duration-300 ease-out mt-6 text-center disabled:opacity-50"
                >
                  Back to Login
                </button>
              </form>
            ) : (
              <form onSubmit={handleAuth} className="flex flex-col gap-4">
                <input
                  type="email"
                  placeholder="operator@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="email"
                  className="w-full bg-black/50 border border-white/5 rounded-xl px-4 py-3 text-white focus:border-white/20 outline-none transition-colors duration-300 ease-out placeholder:text-zinc-600 disabled:opacity-50"
                />
                <div className="flex flex-col w-full">
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete={isSignUp ? "new-password" : "current-password"}
                    className="w-full bg-black/50 border border-white/5 rounded-xl px-4 py-3 text-white focus:border-white/20 outline-none transition-colors duration-300 ease-out placeholder:text-zinc-600 disabled:opacity-50"
                  />
                  {!isSignUp && (
                    <div className="flex justify-end mt-1.5">
                      <button
                        type="button"
                        onClick={() => { setIsResetting(true); setError(null); }}
                        tabIndex="-1"
                        className="text-xs font-medium text-zinc-500 hover:text-zinc-300 transition-colors duration-300 ease-out"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                </div>

                {isSignUp && (
                  <div className="flex items-start gap-3 mt-1">
                    <div className="flex items-center h-5 mt-0.5">
                      <input
                        id="terms"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        required={isSignUp}
                        className="w-4 h-4 rounded border-white/20 bg-black/50 text-white cursor-pointer accent-white"
                      />
                    </div>
                    <div className="text-xs text-zinc-400 leading-relaxed">
                      <label htmlFor="terms" className="cursor-pointer">I have read and agree to the </label>
                      <a href="https://gettermscdn.com/view/TeanA/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white underline decoration-zinc-600 underline-offset-2">Terms of Service</a>
                      {', '}
                      <a href="https://gettermscdn.com/view/TeanA/privacy/en-us" target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white underline decoration-zinc-600 underline-offset-2">Privacy Policy</a>
                      {', and '}
                      <a href="https://gettermscdn.com/view/TeanA/acceptable-use/en-us" target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-white underline decoration-zinc-600 underline-offset-2">Acceptable Use Policy</a>
                      .
                    </div>
                  </div>
                )}

                {error && <div className="text-red-400 text-sm font-medium text-center">{error}</div>}

                <button
                  type="submit"
                  disabled={loading || (isSignUp && !agreedToTerms)}
                  className="w-full bg-white text-black font-semibold tracking-wide py-3.5 rounded-xl mt-4 hover:bg-zinc-200 transition-colors duration-300 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : (isSignUp ? 'Create Account' : 'Sign In')}
                </button>
              </form>
            )}

            {!isResetting && (
              <button
                type="button"
                onClick={() => navigate(isSignUp ? '/login' : '/signup')}
                disabled={loading}
                className="text-xs text-zinc-500 hover:text-white transition-colors duration-300 ease-out mt-6 text-center disabled:opacity-50"
              >
                {isSignUp ? 'Already have an account? Sign in.' : "Don't have an account? Create one."}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}